/**
 * Wave 29 Chunk 9b — Upload a candidate CV (full page).
 *
 * Flow:
 *  1. User selects a PDF / image CV (capture allowed on mobile).
 *  2. POST /api/uploads → returns upload_id.
 *  3. POST /api/recruitment/candidates with the upload_id → triggers
 *     GPT-5.2 Vision OCR + automatic ranking against any selected positions.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { UserPlus, ChevronLeft, Loader2, Save, FileText } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "../components/ui/sonner";
import VoiceTextarea from "../components/VoiceTextarea";

export default function RecruitmentCandidateNew() {
  const { user } = useAuth();
  const isHr = user && ["owner", "admin"].includes(user.role);
  const nav = useNavigate();
  const [params] = useSearchParams();
  const initialPosition = params.get("position");

  const [positions, setPositions] = useState([]);
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", note: "",
    position_ids: initialPosition ? [initialPosition] : [],
  });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user || !isHr) return;
    (async () => {
      try {
        const { data } = await api.get("/recruitment/positions");
        setPositions(data?.items || []);
      } catch (e) { /* silent */ }
    })();
  }, [user, isHr]);

  const togglePos = (pid) => {
    setForm((f) => {
      const sel = new Set(f.position_ids || []);
      if (sel.has(pid)) sel.delete(pid);
      else sel.add(pid);
      return { ...f, position_ids: Array.from(sel) };
    });
  };

  const fileLabel = useMemo(() => file ? `${file.name} (${Math.round(file.size / 1024)} KB)` : "Choose a CV file (PDF / image)…", [file]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!file && !form.full_name.trim()) {
      return toast.error("Either upload a CV file OR enter a name first.");
    }
    setBusy(true);
    try {
      let uploadId = null;
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("source", "recruitment_cv");
        const up = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 0 });
        uploadId = up?.data?.id;
      }
      const body = {
        full_name: form.full_name.trim() || null,
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        note: form.note.trim() || null,
        position_ids: form.position_ids,
        upload_id: uploadId,
      };
      const { data } = await api.post("/recruitment/candidates", body);
      toast.success("Candidate saved" + (uploadId ? " — CV parsing complete" : ""));
      nav(`/recruitment/candidates/${data?.id}`);
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Save failed.");
    } finally { setBusy(false); }
  };

  if (!isHr) return <div className="hv-card p-6 text-sm text-[#dde3ec]">HR only.</div>;

  return (
    <div className="space-y-4 max-w-2xl" data-testid="candidate-new-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Link to="/recruitment" className="hv-btn hv-btn-ghost text-xs" data-testid="candidate-new-back">
          <ChevronLeft className="w-3.5 h-3.5" /> Back
        </Link>
        <h1 className="font-display text-xl sm:text-2xl font-bold flex items-center gap-2">
          <UserPlus className="w-5 h-5 text-[#1FB6C1]" /> Upload CV
        </h1>
        <span />
      </div>

      <form onSubmit={submit} className="hv-card p-4 sm:p-5 space-y-4" data-testid="candidate-new-form">
        <label className="block">
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087] mb-1">CV file (PDF or image)</div>
          <label className="flex items-center gap-2 hv-input cursor-pointer text-sm" data-testid="candidate-file-label">
            <FileText className="w-4 h-4 text-[#1FB6C1]" />
            <span className={file ? "text-white" : "text-[#9aa3b5]"}>{fileLabel}</span>
            <input
              type="file"
              accept=".pdf,image/*"
              className="hidden"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              data-testid="candidate-file-input"
            />
          </label>
          <p className="text-[11px] text-[#9aa3b5] mt-1">Hidro will OCR the CV and score it against every position you link.</p>
        </label>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Full name (optional — auto-filled from CV)">
            <input type="text" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })}
              className="hv-input text-sm w-full" data-testid="candidate-name" />
          </Field>
          <Field label="Email">
            <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
              className="hv-input text-sm w-full" data-testid="candidate-email" />
          </Field>
        </div>

        <Field label="Phone">
          <input type="text" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
            className="hv-input text-sm w-full" data-testid="candidate-phone" />
        </Field>

        <Field label="Notes">
          <VoiceTextarea rows={3} value={form.note} onChange={(v) => setForm({...form, note: v})} data-testid="candidate-note"
            placeholder="e.g. Referred by Jaime · Available immediately"  testIdPrefix="candidate-note-voice" context="note" />
        </Field>

        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087] mb-1">
            Link to positions ({form.position_ids.length} selected)
          </div>
          {positions.length === 0 ? (
            <p className="text-xs text-[#9aa3b5]">No positions yet — <Link className="text-[#1FB6C1] hover:underline" to="/recruitment/positions/new">create one first</Link>.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
              {positions.map((p) => {
                const checked = form.position_ids.includes(p.id);
                return (
                  <label key={p.id} className="flex items-center gap-2 text-xs text-[#dde3ec] cursor-pointer bg-white/[0.02] hover:bg-white/[0.05] rounded px-2 py-1.5">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => togglePos(p.id)}
                      data-testid={`candidate-position-${p.id}`}
                    />
                    <span className="truncate">{p.title}</span>
                    <span className="text-[10px] text-[#647087] ml-auto">{p.status}</span>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 pt-2 border-t border-white/5">
          <Link to="/recruitment" className="hv-btn hv-btn-ghost text-xs">Cancel</Link>
          <button type="submit" disabled={busy} className="hv-btn hv-btn-primary text-xs disabled:opacity-50" data-testid="candidate-save">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            Save candidate
          </button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] uppercase tracking-[0.2em] text-[#647087] mb-1">{label}</div>
      {children}
    </label>
  );
}
